'use client';

import Icon from '@ant-design/icons';
import { Modal } from 'antd';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import AddSvg from '@/components/Icon/svgs/add.svg';
import RemoveSvg from '@/components/Icon/svgs/mi_remove.svg';
import RightSvg from '@/components/Icon/svgs/right.svg';
import LottieJumpLoading from '@/components/chat-app/components/LottieJumpLoading';
import { useTranslation } from '@/i18n/client';
import type { ICircleSettingDetail, IGroupUserInfo } from '@/service/friend';
import { getCircleMemberList } from '@/service/friend';

import AdminSelectView from './AdminSelectView';
import styles from './index.less';

// 阻止事件冒泡到父组件
const stopPropagationModalRender = (node: ReactNode) => (
  <div className="w-full h-full" onClick={(e) => e.stopPropagation()}>
    {node}
  </div>
);

interface IProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  circleRoute: string;
  circleDetail: ICircleSettingDetail | null;
  handleUpdate: () => void;
}

type ViewType = 'main' | 'add' | 'remove';

export const GroupAdminModal = (props: IProps): JSX.Element => {
  const { open, setOpen, circleRoute, circleDetail, handleUpdate } = props;
  const { t } = useTranslation();

  // 当前视图状态
  const [currentView, setCurrentView] = useState<ViewType>('main');
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState<'forward' | 'backward'>('forward');

  // 管理员列表
  const [adminList, setAdminList] = useState<IGroupUserInfo[]>([]);
  const [adminLoading, setAdminLoading] = useState(false);

  // 关闭弹窗
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  // 获取管理员列表
  const fetchAdminList = useCallback(() => {
    if (!circleRoute) return;

    setAdminLoading(true);
    getCircleMemberList({
      route: circleRoute,
      adminOnly: true
    })
      .then((res) => {
        if (res.data.code === 0) {
          setAdminList(res.data.data.list);
        }
      })
      .catch((err) => {
        console.error('Failed to get admin list:', err);
      })
      .finally(() => {
        setAdminLoading(false);
      });
  }, [circleRoute]);

  // 最大可添加的管理员数量
  const maxAddCount = useMemo(() => {
    const maxAdminCount = circleDetail?.adminSettings.maxAdminCount || 5;
    const currentAdminCount = circleDetail?.adminSettings.adminCount || 0;

    return maxAdminCount - currentAdminCount;
  }, [circleDetail?.adminSettings]);

  // 重置状态
  const resetState = useCallback(() => {
    setCurrentView('main');
    setIsAnimating(false);
  }, []);

  useEffect(() => {
    if (open) {
      resetState();
      fetchAdminList();
    }
  }, [open, resetState, fetchAdminList]);

  // 切换到 Add/Remove 视图
  const handleNavigateTo = (view: 'add' | 'remove') => {
    setAnimationDirection('forward');
    setIsAnimating(true);

    setTimeout(() => {
      setCurrentView(view);
      setIsAnimating(false);
    }, 300);
  };

  // 返回主视图
  const handleBack = useCallback(() => {
    setAnimationDirection('backward');
    setIsAnimating(true);

    setTimeout(() => {
      setCurrentView('main');
      setIsAnimating(false);
    }, 300);
  }, []);

  // 操作成功回调
  const handleSuccess = useCallback(() => {
    handleUpdate();
    fetchAdminList();
    handleBack();
  }, [handleUpdate, fetchAdminList, handleBack]);

  // 渲染主视图
  const renderMainView = () => (
    <div
      className={classNames(styles.mainContainer, {
        [styles.slideOut]: isAnimating && animationDirection === 'forward',
        [styles.slideIn]: !isAnimating && animationDirection === 'backward'
      })}
    >
      {/* 头部 */}
      <div className={styles.header}>
        <div className={styles.backBtn} onClick={handleClose}>
          <Icon className={styles.backIcon} component={RightSvg} />
        </div>
        <span className={styles.title}>
          {t('circle.groupAdmins')} ({circleDetail?.adminSettings.adminCount || 0}/
          {circleDetail?.adminSettings.maxAdminCount || 5})
        </span>
        <div className={styles.placeholder} />
      </div>

      {/* 管理员成员卡片 */}
      <div className={styles.membersCard}>
        {adminLoading ? (
          <LottieJumpLoading className="!w-12 !h-12" />
        ) : (
          <div className={styles.membersRow}>
            {/* 现有管理员 */}
            {adminList.map((member) => (
              <div key={member.id} className={styles.memberItem}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img alt={member.username} className={styles.memberAvatar} src={member.avatar} />
                <span className={styles.memberName}>{member.nickname || member.username}</span>
              </div>
            ))}

            {/* Add 按钮 */}
            {maxAddCount > 0 && (
              <div
                className={classNames(styles.memberItem, styles.actionItem)}
                onClick={() => handleNavigateTo('add')}
              >
                <div className={styles.actionAvatar}>
                  <Icon className={styles.actionIcon} component={AddSvg} />
                </div>
                <span className={styles.memberName}>{t('circle.add')}</span>
              </div>
            )}

            {/* Remove 按钮 */}
            {adminList.length > 0 && (
              <div
                className={classNames(styles.memberItem, styles.actionItem)}
                onClick={() => handleNavigateTo('remove')}
              >
                <div className={styles.actionAvatar}>
                  <Icon className={styles.actionIcon} component={RemoveSvg} />
                </div>
                <span className={styles.memberName}>{t('circle.remove')}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 权限说明 */}
      <div className={styles.permissionsCard}>
        <p className={styles.permissionsTitle}>{t('circle.adminPermissionsTitle')}</p>
        <ul className={styles.permissionsList}>
          <li>{t('circle.adminPermission1')}</li>
          <li>{t('circle.adminPermission2')}</li>
          <li>{t('circle.adminPermission3')}</li>
          <li>{t('circle.adminPermission4')}</li>
        </ul>
        <p className={styles.ownerNote}>{t('circle.ownerOnlyNote')}</p>
      </div>
    </div>
  );

  return (
    <Modal
      centered
      className={styles.groupAdminModal}
      closable={false}
      destroyOnClose
      footer={null}
      maskClosable
      modalRender={stopPropagationModalRender}
      onCancel={handleClose}
      open={open}
      width={1000}
      zIndex={1100}
    >
      {currentView === 'main' && renderMainView()}
      {(currentView === 'add' || currentView === 'remove') && (
        <AdminSelectView
          animationDirection={animationDirection}
          circleDetail={circleDetail}
          circleRoute={circleRoute}
          isAnimating={isAnimating}
          maxAddCount={maxAddCount}
          mode={currentView}
          onBack={handleBack}
          onSuccess={handleSuccess}
        />
      )}
    </Modal>
  );
};

export default GroupAdminModal;
